import React, { useEffect, useState } from 'react';
import {
  PDF_CHROME_ITEMS,
  DEFAULT_PDF_CHROME_ITEMS,
  loadPdfExportOpts,
  savePdfExportOpts,
} from './gaPdfExport';

const radio = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #e2e8f0',
  cursor: 'pointer',
  background: '#fff',
};

/**
 * Ask which chrome to stamp, and whether to walk every slide.
 */
const GaPdfExportModal = ({
  open,
  onClose,
  onConfirm,
  exporting,
  progress,
  pageCount = 1,
  activePage = 1,
  preview = {},
}) => {
  const [opts, setOpts] = useState(() => loadPdfExportOpts());

  useEffect(() => {
    if (open) setOpts(loadPdfExportOpts());
  }, [open]);

  if (!open) return null;

  const total = Math.max(1, pageCount);
  const setChrome = (includeChrome) => setOpts((prev) => ({ ...prev, includeChrome }));
  const setScope = (pageScope) => setOpts((prev) => ({ ...prev, pageScope }));
  const toggleItem = (key) => setOpts((prev) => ({
    ...prev,
    items: { ...prev.items, [key]: !prev.items[key] },
  }));
  const setAllItems = (on) => setOpts((prev) => ({
    ...prev,
    items: Object.fromEntries(Object.keys(DEFAULT_PDF_CHROME_ITEMS).map((k) => [k, on])),
  }));

  const download = () => {
    const next = savePdfExportOpts(opts);
    onConfirm(next);
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1200,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !exporting) onClose(); }}
    >
      <div
        role="dialog"
        aria-labelledby="ga-pdf-export-title"
        style={{
          background: '#fff', borderRadius: 12, width: 640, maxWidth: '96vw',
          maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{
          padding: '14px 18px', background: '#0f766e', color: '#fff',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
        }}>
          <strong id="ga-pdf-export-title">Download PDF</strong>
          <button
            type="button"
            onClick={onClose}
            disabled={exporting}
            style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: exporting ? 'default' : 'pointer', lineHeight: 1 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ padding: 18, overflow: 'auto', fontSize: 14, color: '#334155', flex: 1, minHeight: 0 }}>
          {exporting ? (
            <div style={{ padding: '28px 8px', textAlign: 'center' }}>
              <div style={{ fontWeight: 800, color: '#0f766e', marginBottom: 8 }}>
                {progress?.total > 1
                  ? `Rendering page ${progress.current} of ${progress.total}…`
                  : 'Rendering PDF…'}
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>
                Stay on this board until the file downloads.
              </div>
            </div>
          ) : (
            <>
              {total > 1 && (
                <section style={{ marginBottom: 18 }}>
                  <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Pages</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ ...radio, borderColor: opts.pageScope === 'all' ? '#0f766e' : '#e2e8f0', background: opts.pageScope === 'all' ? '#f0fdfa' : '#fff' }}>
                      <input type="radio" name="ga-pdf-pages" checked={opts.pageScope === 'all'} onChange={() => setScope('all')} />
                      <span>
                        <strong>All {total} pages</strong>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Every slide in this sequence, in order.</div>
                      </span>
                    </label>
                    <label style={{ ...radio, borderColor: opts.pageScope === 'current' ? '#0f766e' : '#e2e8f0', background: opts.pageScope === 'current' ? '#f0fdfa' : '#fff' }}>
                      <input type="radio" name="ga-pdf-pages" checked={opts.pageScope === 'current'} onChange={() => setScope('current')} />
                      <span>
                        <strong>This page only</strong>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Page {activePage} of {total}.</div>
                      </span>
                    </label>
                  </div>
                </section>
              )}

              <section style={{ marginBottom: 18 }}>
                <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Header and notes</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <label style={{ ...radio, borderColor: !opts.includeChrome ? '#0f766e' : '#e2e8f0', background: !opts.includeChrome ? '#f0fdfa' : '#fff' }}>
                    <input type="radio" name="ga-pdf-chrome" checked={!opts.includeChrome} onChange={() => setChrome(false)} />
                    <span>
                      <strong>Plain PDF</strong>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Drawing and balloons only — no letterhead, legend, or footer.</div>
                    </span>
                  </label>
                  <label style={{ ...radio, borderColor: opts.includeChrome ? '#0f766e' : '#e2e8f0', background: opts.includeChrome ? '#f0fdfa' : '#fff' }}>
                    <input type="radio" name="ga-pdf-chrome" checked={opts.includeChrome} onChange={() => setChrome(true)} />
                    <span>
                      <strong>Include header and notes</strong>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Pick each stamp separately. Last choice is remembered on this computer.</div>
                    </span>
                  </label>
                </div>
              </section>

              <section style={{ opacity: opts.includeChrome ? 1 : 0.45, pointerEvents: opts.includeChrome ? 'auto' : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontWeight: 800, color: '#0f172a' }}>What to stamp on each page</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={() => setAllItems(true)} style={linkBtn}>All</button>
                    <button type="button" onClick={() => setAllItems(false)} style={linkBtn}>None</button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {PDF_CHROME_ITEMS.map((row) => {
                    const sample = preview[row.key] || row.hint;
                    const on = !!opts.items[row.key];
                    return (
                      <label
                        key={row.key}
                        style={{
                          display: 'flex', gap: 10, alignItems: 'flex-start',
                          padding: '8px 10px', borderRadius: 8,
                          border: on ? '1px solid #99f6e4' : '1px solid #e2e8f0',
                          background: on ? '#f8fffe' : '#fff',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleItem(row.key)}
                          style={{ marginTop: 3 }}
                        />
                        <span>
                          <div style={{ fontWeight: 700, color: '#0f172a' }}>{row.label}</div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2, whiteSpace: 'pre-line' }}>{sample}</div>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </div>

        <div style={{
          padding: '12px 18px', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0,
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={exporting}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: exporting ? 'default' : 'pointer', fontWeight: 600 }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={download}
            disabled={exporting}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: '#0f766e', color: '#fff', fontWeight: 800,
              cursor: exporting ? 'default' : 'pointer',
            }}
          >
            {exporting ? 'Exporting…' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

const linkBtn = {
  border: 'none',
  background: 'none',
  color: '#0f766e',
  fontWeight: 700,
  cursor: 'pointer',
  fontSize: 12,
  padding: 0,
};

export default GaPdfExportModal;
